import type { BagelAiFlowEdge, AiFlowLayoutPosition } from '../types/aiFlow';

const COLUMN_WIDTH = 220;
const ROW_HEIGHT = 100;

/**
 * Assign column = longest-path rank from roots, row = stable index within column.
 */
export function computeAiFlowRankLayout(
  nodeIds: string[],
  edges: BagelAiFlowEdge[],
): Map<string, AiFlowLayoutPosition> {
  const incoming = new Map(nodeIds.map(id => [id, [] as string[]]));
  const outgoing = new Map(nodeIds.map(id => [id, [] as string[]]));

  edges.forEach(edge => {
    if (incoming.has(edge.to) && outgoing.has(edge.from)) {
      outgoing.get(edge.from)!.push(edge.to);
      incoming.get(edge.to)!.push(edge.from);
    }
  });

  const rank = new Map<string, number>(nodeIds.map(id => [id, 0]));

  Array.from({ length: nodeIds.length }).forEach(() => {
    edges.forEach(edge => {
      if (rank.has(edge.from) && rank.has(edge.to)) {
        const nextRank = (rank.get(edge.from) ?? 0) + 1;
        if (nextRank > (rank.get(edge.to) ?? 0)) {
          rank.set(edge.to, nextRank);
        }
      }
    });
  });

  const byColumn = nodeIds.reduce<Map<number, string[]>>((acc, id) => {
    const column = rank.get(id) ?? 0;
    const list = acc.get(column) ?? [];
    list.push(id);
    acc.set(column, list);
    return acc;
  }, new Map());

  const orderIndex = new Map(nodeIds.map((id, index) => [id, index]));
  const layout = new Map<string, AiFlowLayoutPosition>();

  Array.from(byColumn.entries()).forEach(([column, ids]) => {
    ids.sort((a, b) => (orderIndex.get(a) ?? 0) - (orderIndex.get(b) ?? 0));
    ids.forEach((id, row) => layout.set(id, { column, row }));
  });

  return layout;
}

export function layoutToPixelPosition(
  layout: AiFlowLayoutPosition,
): { x: number; y: number } {
  return {
    x: layout.column * COLUMN_WIDTH,
    y: layout.row * ROW_HEIGHT,
  };
}

/** Nodes in visual flow order (column-major). */
export function sortNodesByFlowOrder(
  nodeIds: string[],
  layout: Map<string, AiFlowLayoutPosition>,
): string[] {
  return [...nodeIds].sort((a, b) => {
    const la = layout.get(a) ?? { column: 0, row: 0 };
    const lb = layout.get(b) ?? { column: 0, row: 0 };
    if (la.column !== lb.column) return la.column - lb.column;
    return la.row - lb.row;
  });
}

/** Edge keys adjacent in a test path, e.g. "greeting->classifier". */
export function buildHighlightedEdgeKeys(path: string[]): Set<string> {
  return new Set(
    path.slice(0, -1).map((from, index) => `${from}->${path[index + 1]}`),
  );
}

export { COLUMN_WIDTH, ROW_HEIGHT };
